import { supabase } from './supabase'

const KEY = 'kermes.tenantId'

/**
 * Hostnames that are the app itself rather than a yurt. Without this list a
 * deploy at "www.adissoo.com" would read its own first label as the tenant
 * and try to sign people in against a yurt named "www".
 */
const APP_HOSTS = new Set(['www', 'app', 'kasa'])

/** Used when nothing else identifies a yurt: a build with no .env must still
 *  land somewhere real. */
export const FALLBACK_TENANT = 'emirler'

/**
 * First path segments that belong to the app rather than to a yurt.
 *
 * The institution now lives in the path ("/emirler/giris"), so without this
 * list a link handed out before the move, a bare "/giris", would read
 * "giris" as an institution and try to sign someone into a yurt by that name.
 */
const APP_PATHS = new Set([
  'giris',
  'kurulum',
  'm',
  'k',
  'menu',
  'cari',
  'rapor',
  'ayarlar',
  'sistem-9f3a',
  'sifre',
])

/** The tenant in "/emirler/giris", or null on a path that carries none. */
function tenantFromPath(): string | null {
  const first = window.location.pathname.split('/')[1]?.trim().toLowerCase()
  if (!first || APP_PATHS.has(first)) return null
  return first
}

/**
 * Resolves the tenant, in order:
 *   1. path, "/emirler/giris" → "emirler" (skipped for APP_PATHS)
 *   2. ?yurt= query parameter, links handed out before the move
 *   3. subdomain, "emirler.adissoo.com" → "emirler" (skipped for APP_HOSTS)
 *   4. localStorage, remembered from a previous visit or login
 *   5. VITE_DEFAULT_TENANT_ID, local dev and single-tenant deploys
 *
 * Layer 1 is how every yurt shares one domain now. Layers 2 and 3 stay because
 * printed codes and saved links still carry those spellings.
 */
export function resolveTenantId(): string | null {
  const fromPath = tenantFromPath()
  if (fromPath) {
    rememberTenantId(fromPath)
    return fromPath
  }

  const fromQuery = new URLSearchParams(window.location.search).get('yurt')
  if (fromQuery) {
    const cleaned = fromQuery.trim().toLowerCase()
    if (cleaned) {
      rememberTenantId(cleaned)
      return cleaned
    }
  }

  const host = window.location.hostname
  const parts = host.split('.')
  const isLocal = host === 'localhost' || host.startsWith('127.')

  // Cloudflare önizleme adresi (onizleme.yurt-events.pages.dev) kurum değil
  // (28 Eki 2026): ana ekrandan "/" ile açılınca "onizleme" kurum sanılıyordu.
  const isPreview = host.endsWith('.pages.dev')
  if (!isLocal && !isPreview && parts.length >= 3 && !APP_HOSTS.has(parts[0])) {
    return parts[0]
  }

  const stored = localStorage.getItem(KEY)
  // An earlier build read the app's own hostname as a tenant and saved it.
  // Trusting that value would keep sending logins to a yurt that does not
  // exist, on a device that is otherwise fully up to date.
  if (stored && !APP_HOSTS.has(stored)) return stored
  if (stored) localStorage.removeItem(KEY)

  return import.meta.env.VITE_DEFAULT_TENANT_ID || FALLBACK_TENANT
}

export function rememberTenantId(tenantId: string): void {
  localStorage.setItem(KEY, tenantId)
}

export function forgetTenantId(): void {
  localStorage.removeItem(KEY)
}

/**
 * Whether this device already has a valid tenant remembered, synchronous,
 * touches nothing but localStorage. LoginPage uses this to decide, before
 * its first render, whether to show the "kurum kodu" field at all.
 */
export function hasStoredTenant(): boolean {
  const stored = localStorage.getItem(KEY)
  return Boolean(stored && !APP_HOSTS.has(stored))
}

/**
 * Whether the tenant is already pinned by the URL, a path segment, a
 * `?yurt=` link or a subdomain, so the login screen has no reason to ask for
 * a code. Mirrors the first steps of resolveTenantId() on purpose rather than sharing
 * code with it: that function also has the side effect of remembering
 * whatever it resolves, and splitting it would risk its two existing
 * callers in AuthContext.tsx.
 */
export function tenantFromUrl(): boolean {
  if (tenantFromPath()) return true
  if (new URLSearchParams(window.location.search).get('yurt')) return true

  const host = window.location.hostname
  const parts = host.split('.')
  const isLocal = host === 'localhost' || host.startsWith('127.')

  return !isLocal && parts.length >= 3 && !APP_HOSTS.has(parts[0])
}

/**
 * Which institution a sign-in belongs to, asked before the attempt.
 *
 * Only needed on the bare domain: an address that already names the kurum
 * (adissoo.com/emirler/giris) has answered this question before the screen
 * was drawn. Null means nobody is signed up under that identity, the login
 * screen folds that into the same "wrong credentials" message rather than
 * confirming which half was wrong.
 *
 * See supabase/login-lookup.sql for why this is an RPC and not a select.
 */
export async function tenantOfLogin(ident: string): Promise<string | null> {
  if (!supabase) return null

  const cleaned = ident.trim()
  if (!cleaned) return null

  const { data, error } = await supabase.rpc('tenant_of_login', { ident: cleaned })
  if (error) throw error
  return (data as string | null) ?? null
}

/**
 * Whether an institution slug is real. Still used by the setup screen, which
 * has to refuse a taken one before spending an activation code.
 *
 * An RPC rather than a select because `tenants` is closed to anon entirely
 * (grants.sql, rls.sql), see supabase/tenant-lookup.sql.
 */
export async function tenantExists(id: string): Promise<boolean> {
  if (!supabase) return true

  const cleaned = id.trim().toLowerCase()
  if (!cleaned) return false

  const { data, error } = await supabase.rpc('tenant_exists', { t: cleaned })
  if (error) throw error
  return data === true
}

/**
 * The owner changing the institution's contact number from Ayarlar › Kurum.
 * The database checks the shape again (`+90` and 10 digits, see
 * iletisim-telefonu.sql).
 */
export async function saveContactPhone(tenantId: string, phone: string | null): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { error } = await supabase.from('tenants').update({ contact_phone: phone }).eq('id', tenantId)
  if (error) throw new Error('Telefon kaydedilemedi.')
}
